import React, { useState, useEffect } from 'react';
import {
  BookOpen, Plus, Search, ChevronRight, ChevronDown, Loader2,
  Edit2, Trash2, Zap, FolderTree, Filter, Hash, Check, X
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import ConfirmDialog from '../ConfirmDialog';
import { useLoader } from '../../contexts/LoaderContext';

const CLASSES_CONFIG = [
  { num: 1, label: 'Ressources durables', color: '#6366f1' },
  { num: 2, label: 'Actif immobilisé', color: '#3b82f6' },
  { num: 3, label: 'Stocks', color: '#10b981' },
  { num: 4, label: 'Tiers', color: '#f59e0b' },
  { num: 5, label: 'Trésorerie', color: '#06b6d4' },
  { num: 6, label: 'Charges', color: '#ef4444' },
  { num: 7, label: 'Produits', color: '#22c55e' },
  { num: 8, label: 'Comptes spéciaux', color: '#8b5cf6' },
];

const TYPE_COLORS = {
  actif: { color: '#3b82f6', bg: '#eff6ff', label: 'Actif' },
  passif: { color: '#8b5cf6', bg: '#ede9fe', label: 'Passif' },
  charge: { color: '#ef4444', bg: '#fee2e2', label: 'Charge' },
  produit: { color: '#10b981', bg: '#d4f4dd', label: 'Produit' },
};

const PlanComptableModule = () => {
  const [comptes, setComptes] = useState([]);
  const [totaux, setTotaux] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClasse, setFilterClasse] = useState('');
  const [filterType, setFilterType] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Arborescence
  const [expandedClasses, setExpandedClasses] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingCompte, setEditingCompte] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    numero_compte: '', intitule: '', type_compte: 'charge', parent_numero: '', notes: ''
  });

  const [confirmDialogConfig, setConfirmDialogConfig] = useState(null);

  const { showLoader, hideLoader } = useLoader();
  const showToast = (m, t = 'success') => setToast({ show: true, message: m, type: t });

  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  useEffect(() => { initAndLoad(); }, []);
  useEffect(() => { if (isInitialized) loadComptes(); }, [filterClasse, filterType]);
  useEffect(() => { if (isInitialized) { const t = setTimeout(() => loadComptes(), 300); return () => clearTimeout(t); } }, [searchTerm]);

  const initAndLoad = async () => {
    showLoader(); setIsLoading(true);
    try {
      await api.planComptable.init();
      setIsInitialized(true);
      await loadComptes();
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); hideLoader(); }
  };

  const loadComptes = async () => {
    showLoader();
    try {
      const r = await api.planComptable.getAll({ classe: filterClasse, search: searchTerm, type_compte: filterType });
      if (r.success) { setComptes(r.data); setTotaux(r.totaux || {}); }
    } catch (e) { console.error(e); }
    finally { hideLoader(); }
  };

  // ============================================================================
  // CRUD
  // ============================================================================
  const openCreateModal = (parentNumero = '') => {
    const parentCompte = comptes.find(c => c.numero_compte === parentNumero);
    setModalMode('create'); setEditingCompte(null);
    setFormData({
      numero_compte: parentNumero, intitule: '',
      type_compte: parentCompte ? parentCompte.type_compte : 'charge',
      parent_numero: parentNumero, notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (c) => {
    setModalMode('edit'); setEditingCompte(c);
    setFormData({ numero_compte: c.numero_compte, intitule: c.intitule, type_compte: c.type_compte, parent_numero: c.parent_numero || '', notes: c.notes || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.numero_compte.trim() || !formData.intitule.trim()) { showToast('Numéro et intitulé obligatoires', 'error'); return; }
    setIsSaving(true);
    try {
      const r = modalMode === 'create'
        ? await api.planComptable.creer(formData)
        : await api.planComptable.modifier(editingCompte.id, formData);
      if (r.success) { showToast(r.message); setShowModal(false); await loadComptes(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async (c) => {
    try {
      const r = await api.planComptable.supprimer(c.id);
      if (r.success) { showToast(r.message); await loadComptes(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
  };

  // ============================================================================
  // ARBORESCENCE
  // ============================================================================
  const toggleClasse = (num) => {
    const ns = new Set(expandedClasses);
    ns.has(num) ? ns.delete(num) : ns.add(num);
    setExpandedClasses(ns);
  };
  const toggleCategorie = (num) => {
    const ns = new Set(expandedCategories);
    ns.has(num) ? ns.delete(num) : ns.add(num);
    setExpandedCategories(ns);
  };

  const buildTree = () => {
    const tree = [];
    const classes = [...new Set(comptes.map(c => c.classe))].sort();
    for (const cl of classes) {
      const classComptes = comptes.filter(c => c.classe === cl);
      const root = classComptes.find(c => c.niveau === 1);
      const categories = classComptes.filter(c => c.niveau === 2);
      const sousComptes = classComptes.filter(c => c.niveau >= 3);

      tree.push({
        classe: cl, root, categories: categories.map(cat => ({
          ...cat,
          children: sousComptes.filter(s => s.parent_numero === cat.numero_compte)
        }))
      });
    }
    return tree;
  };

  const inputStyle = {
    onFocus: (e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; },
    onBlur: (e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; },
  };

  const isSearchMode = searchTerm.length > 0;

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><BookOpen size={18} style={{ color: '#448696' }} /><span className="text-xs font-semibold text-gray-500">Total comptes</span></div>
          <div className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{totaux.total || 0}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #3b82f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><FolderTree size={18} className="text-blue-600" /><span className="text-xs font-semibold text-gray-500">Bilan</span></div>
          <div className="text-2xl font-bold text-blue-600">{totaux.nb_bilan || 0}</div>
          <div className="text-xs text-gray-400 mt-0.5">Classes 1–5</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #ef4444', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Hash size={18} className="text-red-600" /><span className="text-xs font-semibold text-gray-500">Résultat</span></div>
          <div className="text-2xl font-bold text-red-600">{totaux.nb_resultat || 0}</div>
          <div className="text-xs text-gray-400 mt-0.5">Classes 6–7</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Check size={18} className="text-green-600" /><span className="text-xs font-semibold text-gray-500">Utilisables</span></div>
          <div className="text-2xl font-bold text-green-600">{totaux.nb_utilisables || 0}</div>
        </div>
        <div onClick={() => openCreateModal()} className="bg-white rounded-2xl p-5 cursor-pointer group transition-all hover:shadow-md" style={{ border: '2px dashed #d1d5db' }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#448696'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}>
          <div className="flex items-center justify-center h-full gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#f0f9fb' }}><Plus size={22} style={{ color: '#448696' }} /></div>
            <div><div className="text-sm font-bold text-gray-700 group-hover:text-[#448696]">Nouveau</div><div className="text-xs text-gray-400">Ajouter un compte</div></div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input type="text" placeholder="Rechercher par numéro ou intitulé..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select value={filterClasse} onChange={(e) => setFilterClasse(e.target.value)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 focus:outline-none">
              <option value="">Toutes classes</option>
              {CLASSES_CONFIG.map(c => <option key={c.num} value={c.num}>Classe {c.num} — {c.label}</option>)}
            </select>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold border-2 border-gray-200 text-gray-600 focus:outline-none">
              <option value="">Tous types</option>
              <option value="actif">Actif</option>
              <option value="passif">Passif</option>
              <option value="charge">Charge</option>
              <option value="produit">Produit</option>
            </select>
          </div>
        </div>
      </div>

      {/* Arborescence ou liste plate (si recherche) */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
      ) : comptes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">Aucun compte</h3>
        </div>
      ) : isSearchMode ? (
        /* Liste plate pour la recherche */
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="divide-y divide-gray-50">
            {comptes.map(c => {
              const tc = TYPE_COLORS[c.type_compte] || TYPE_COLORS.charge;
              const cc = CLASSES_CONFIG.find(x => x.num === c.classe);
              return (
                <div key={c.id} className="px-6 py-3 hover:bg-gray-50 flex items-center gap-4 group">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cc?.color || '#9ca3af' }} />
                  <span className="text-sm font-mono font-bold w-16" style={{ color: cc?.color || '#374151' }}>{c.numero_compte}</span>
                  <span className="text-sm text-gray-800 flex-1" style={{ paddingLeft: `${(c.niveau - 1) * 12}px` }}>{c.intitule}</span>
                  <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: tc.bg, color: tc.color }}>{tc.label}</span>
                  {!c.est_systeme && (
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); openEditModal(c); }} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-100"><Edit2 size={12} className="text-gray-400" /></button>
                      <button onClick={(e) => { e.stopPropagation(); setConfirmDialogConfig(c); }} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50"><Trash2 size={12} className="text-red-400" /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Vue arborescente */
        <div className="space-y-3">
          {buildTree().map(({ classe, root, categories }) => {
            const cc = CLASSES_CONFIG.find(x => x.num === classe);
            const isExpanded = expandedClasses.has(classe);
            return (
              <div key={classe} className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                {/* Classe header */}
                <div onClick={() => toggleClasse(classe)} className="px-6 py-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors">
                  {isExpanded ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: cc?.color || '#6b7280' }}>
                    {classe}
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-bold" style={{ color: cc?.color || '#374151' }}>{root?.intitule || `Classe ${classe}`}</span>
                    <span className="text-xs text-gray-400 ml-3">{categories.length} catégories</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); openCreateModal(String(classe)); }}
                    className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"><Plus size={14} style={{ color: cc?.color }} /></button>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {categories.map(cat => {
                      const isCatExpanded = expandedCategories.has(cat.numero_compte);
                      const tc = TYPE_COLORS[cat.type_compte] || TYPE_COLORS.charge;
                      return (
                        <div key={cat.id}>
                          <div onClick={() => toggleCategorie(cat.numero_compte)} className="px-6 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 pl-14 group">
                            {cat.children.length > 0 ? (isCatExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />) : <div className="w-3.5" />}
                            <span className="text-xs font-mono font-bold w-10" style={{ color: cc?.color }}>{cat.numero_compte}</span>
                            <span className="text-sm text-gray-800 flex-1">{cat.intitule}</span>
                            <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: tc.bg, color: tc.color }}>{tc.label}</span>
                            <span className="text-xs text-gray-400">{cat.children.length > 0 ? `${cat.children.length} comptes` : ''}</span>
                            <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                              <button onClick={(e) => { e.stopPropagation(); openCreateModal(cat.numero_compte); }} className="w-6 h-6 rounded flex items-center justify-center hover:bg-gray-100"><Plus size={12} style={{ color: cc?.color }} /></button>
                            </div>
                          </div>

                          {isCatExpanded && cat.children.map(child => {
                            const ctc = TYPE_COLORS[child.type_compte] || TYPE_COLORS.charge;
                            return (
                              <div key={child.id} className="px-6 py-2.5 flex items-center gap-3 hover:bg-gray-50 pl-24 group">
                                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cc?.color }} />
                                <span className="text-xs font-mono font-bold w-12" style={{ color: '#6b7280' }}>{child.numero_compte}</span>
                                <span className="text-sm text-gray-700 flex-1">{child.intitule}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: ctc.bg, color: ctc.color }}>{ctc.label}</span>
                                {child.est_utilisable ? <Check size={12} className="text-green-500" /> : <X size={12} className="text-gray-300" />}
                                {!child.est_systeme && (
                                  <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                                    <button onClick={() => openEditModal(child)} className="w-6 h-6 rounded flex items-center justify-center hover:bg-gray-100"><Edit2 size={11} className="text-gray-400" /></button>
                                    <button onClick={() => setConfirmDialogConfig(child)} className="w-6 h-6 rounded flex items-center justify-center hover:bg-red-50"><Trash2 size={11} className="text-red-400" /></button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="px-6 py-4 rounded-t-2xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              {modalMode === 'create' ? <Plus size={22} className="text-white" /> : <Edit2 size={22} className="text-white" />}
              <h3 className="text-lg font-bold text-white">{modalMode === 'create' ? 'Nouveau compte' : 'Modifier'}</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">N° compte *</label>
                  <input type="text" value={formData.numero_compte} onChange={(e) => setFormData(f => ({ ...f, numero_compte: e.target.value }))}
                    disabled={modalMode === 'edit'} placeholder="601..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-mono font-bold text-sm focus:outline-none disabled:bg-gray-100" {...inputStyle} /></div>
                <div><label className="block text-sm font-semibold text-gray-700 mb-2">Type *</label>
                  <select value={formData.type_compte} onChange={(e) => setFormData(f => ({ ...f, type_compte: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none">
                    {Object.entries(TYPE_COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select></div>
              </div>
              <div className="mb-4"><label className="block text-sm font-semibold text-gray-700 mb-2">Intitulé *</label>
                <input type="text" value={formData.intitule} onChange={(e) => setFormData(f => ({ ...f, intitule: e.target.value }))} placeholder="Intitulé du compte..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
              <div className="mb-6"><label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))} rows="2"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-sm focus:outline-none" {...inputStyle} /></div>
              <div className="flex gap-3">
                <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Annuler</button>
                <button onClick={handleSave} disabled={isSaving} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50" style={{ backgroundColor: '#448696' }}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (modalMode === 'create' ? 'Créer' : 'Enregistrer')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDialogConfig && (
        <ConfirmDialog isOpen={true} title="Supprimer ce compte" description={`Supprimer "${confirmDialogConfig.numero_compte} — ${confirmDialogConfig.intitule}" ?`}
          type="danger" confirmText="Supprimer" cancelText="Annuler"
          onClose={() => setConfirmDialogConfig(null)}
          onConfirm={async () => { await handleDelete(confirmDialogConfig); setConfirmDialogConfig(null); }} />
      )}

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>
  );
};

export default PlanComptableModule;